'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/reorder/styles.css';
import { ComponentUsageError } from 'BomdisoftComponents/errors/index';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { type ReorderAxis, type ReorderTreatment, useReorder } from 'BomdisoftComponents/reorder/use-reorder';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type { ReorderAxis, ReorderTreatment };

export type ReorderLabels = {
  /** Names the action on the grip. Used for both the tooltip and the aria-label,
   *  which the icon-only-button rule requires to agree. */
  grip: (name: string) => string;
  /** The word drawn inside the reserved slot. */
  slot: string;
  /** Announced on every move, and on pick up and put down. */
  announce: (name: string, position: number, total: number) => string;
};

export type ReorderItemDescriptor = {
  id: string;
  /** The item's accessible name, already translated. */
  name: string;
  /** Applied to the element that IS the grid item, which is this component's own
   *  wrapper rather than anything the render function returns. A tile that spans
   *  two tracks must carry its `grid-column` here or the span is dropped: a rule
   *  on an inner node styles a child of the grid item, which the grid ignores.
   *  A class rather than a number so the consumer keeps the span in CSS, where a
   *  container query can retract it on a container too narrow for two tracks. */
  className?: string;
};

export type ReorderProps = {
  axis?: ReorderAxis;
  className?: string;
  items: readonly ReorderItemDescriptor[];
  labels: ReorderLabels;
  onReorder: (ids: readonly string[]) => void;
  /** How the destination is drawn. Boards open a reserved slot; a table keeps
   *  the insertion rule, because a column is one width shared by every row. */
  treatment?: ReorderTreatment;
  children: (item: ReorderItemDescriptor, index: number) => ReactNode;
  testId?: string;
};

type GripContextValue = {
  id: string;
  name: string;
  labels: ReorderLabels;
  held: boolean;
  gripProps: Record<string, unknown>;
};

const GripContext = createContext<GripContextValue | null>(null);

/**
 * Drag to reorder, for a wrapping grid or a single-axis list.
 *
 * Built on the platform's drag and drop rather than on pointer events, which
 * keeps the JavaScript to one handler per event and hands the browser the work
 * it already does: it draws the drag image, and it decides which element the
 * cursor is over. That second point is not a convenience. Computing the drop
 * target from geometry means choosing a switch point, and the obvious one, the
 * target's midpoint, leaves the indicator half an item behind the cursor:
 * measured at 117px of overshoot on a 242px tile and 231px on a two-track one.
 * `dragover` fires on the element under the cursor, so the indicator moves the
 * moment you cross into a neighbour and there is no geometry to get wrong.
 *
 * Only the grip starts a drag, so the rest of the item keeps whatever it already
 * does on click.
 *
 * Touch gets a tap rather than a drag, because a touch fires `pointerdown`,
 * `touchstart` and `contextmenu` and no drag events at all: tap the grip to pick
 * up, tap a destination to place. Measured, not assumed, and confirmed
 * independently by the prodantix lane against a mouse control on the same page.
 *
 * ## Adopting this in an existing surface
 *
 * Four things bite consumers, all of them reported from real adoptions:
 *
 * 1. **This component's wrapper is the grid or flex item, not your markup.** A
 *    `grid-column: span 2` or a `block-size` on your own node styles a CHILD of
 *    the item and the layout never sees it. Put the class on the descriptor's
 *    `className` instead. The wrapper also stretches to the tallest item in the
 *    row, so your own node usually wants `block-size: 100%` or it leaves a gap
 *    underneath itself.
 * 2. **`ReorderGrip` throws without a `Reorder` above it.** It used to return
 *    null, which drew a board with no handles and left a spec that mounted the
 *    child alone entirely green. Consumer specs must wrap in a real `Reorder`.
 * 3. **The touch hit area can be clipped by an ancestor that scrolls.** The grip
 *    draws at 16x20 and grows to the 44x44 minimum through a centred overlay, so
 *    it extends 14px past the handle on every side. An ancestor with `overflow`
 *    of `auto` or `hidden` clips that, and `overflow: auto` on a page scroller
 *    reads as harmless while doing it. Only bites when the grip sits flush
 *    against that ancestor's edge; give it inset room there.
 * 4. **A tripwire asserting a withdrawn testid is ABSENT can never fail again**
 *    once the control is renamed. Assert `reorder-grip-<id>` is PRESENT instead.
 */
export const Reorder = ({ axis = 'list', className, items, labels, onReorder, treatment = 'slot', children, testId }: ReorderProps) => {
  const [announcement, setAnnouncement] = useState('');
  const ids = useMemo(() => items.map(item => item.id), [items]);
  const nameOf = useCallback((id: string) => items.find(item => item.id === id)?.name, [items]);

  const announce = useCallback(
    (id: string, position: number, total: number) => {
      const name = nameOf(id);
      if (name) setAnnouncement(labels.announce(name, position, total));
    },
    [labels, nameOf],
  );

  const { dragging, getGripProps, getItemProps, getSurfaceProps, held, slotIndex } = useReorder({ announce, axis, ids, onReorder, treatment });

  const slot = treatment === 'slot' && dragging && slotIndex !== null ? <ReorderSlot key='reorder-slot' label={labels.slot} surfaceProps={getSurfaceProps()} /> : null;

  // The dragged item stays MOUNTED and leaves the layout instead, because the
  // browser fires `dragend` on the source element: unmount it and a drag that
  // ends without a drop can never clean up after itself.
  const nodes: ReactNode[] = [];
  let placed = 0;
  for (const item of items) {
    const isDragging = item.id === dragging;
    if (slot && !isDragging && placed === slotIndex) nodes.push(slot);
    if (!isDragging) placed += 1;
    nodes.push(
      <ReorderItem dragging={isDragging} gripProps={getGripProps(item.id)} held={held === item.id} index={ids.indexOf(item.id)} item={item} itemProps={getItemProps(item.id)} key={item.id} labels={labels} treatment={treatment}>
        {children}
      </ReorderItem>,
    );
  }
  if (slot && slotIndex !== null && slotIndex >= placed) nodes.push(slot);

  return (
    /* The container is a drop target too. A board is rarely full, so releasing
       a tile into the empty space below the last row is an ordinary thing to do,
       and it used to be refused in silence. */
    <div className={className} data-testid={testId} {...getSurfaceProps()}>
      {nodes}
      <span aria-live='polite' className='bomdisoft-reorder-live' data-testid={testId ? `${testId}-live` : undefined}>
        {announcement}
      </span>
    </div>
  );
};

type ReorderItemProps = {
  children: (item: ReorderItemDescriptor, index: number) => ReactNode;
  dragging: boolean;
  gripProps: Record<string, unknown>;
  held: boolean;
  index: number;
  item: ReorderItemDescriptor;
  itemProps: Record<string, unknown>;
  labels: ReorderLabels;
  treatment: ReorderTreatment;
};

const ReorderItem = ({ children, dragging, gripProps, held, index, item, itemProps, labels, treatment }: ReorderItemProps) => {
  const grip = useMemo<GripContextValue>(() => ({ gripProps, held, id: item.id, labels, name: item.name }), [gripProps, held, item.id, item.name, labels]);
  const { ref, ...rest } = itemProps as { ref: (element: HTMLElement | null) => void };

  return (
    <GripContext.Provider value={grip}>
      {/* The drop handlers arrive through a spread, so Biome's a11y rules no
          longer see them and the suppressions this used to carry are dead. That
          is the linter losing sight of the code, not the concern going away, so
          the answer is written down instead: the wrapper is a drop target only,
          and the whole gesture has a keyboard equivalent on the grip inside it,
          which is a real button carrying Space, the arrows and Escape. Giving
          this div an interactive role would claim a focusable control that does
          not exist and add a tab stop to every item.

          The rule exists so a mouse-only handler cannot strand a keyboard user.
          That is answered here rather than suppressed: the wrapper is a drop
          target only, and the whole gesture has a keyboard equivalent on the
          grip inside it, which is a real button carrying Space, the arrows and
          Escape. Giving this div an interactive role would claim a focusable
          control that does not exist and add a tab stop to every item. */}
      <div className={joinClasses('bomdisoft-reorder-item', item.className)} data-testid={`reorder-item-${item.id}`} data-treatment={treatment} ref={ref} {...rest}>
        {children(item, index)}
      </div>
    </GripContext.Provider>
  );
};

/** The destination, drawn rather than left blank: an empty gap in a grid reads as
 *  a layout that has broken instead of a place being held. It needs no size of
 *  its own, because it is a grid or flex item like any other. */
const ReorderSlot = ({ label, surfaceProps }: { label: string; surfaceProps: Record<string, unknown> }) => (
  <div aria-hidden='true' className='bomdisoft-reorder-slot' data-testid='reorder-slot' {...surfaceProps}>
    <span className='bomdisoft-reorder-slot-label'>{label}</span>
  </div>
);

export type ReorderGripProps = {
  className?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
};

/** The only thing that starts a drag, and the focus target for the keyboard
 *  path, so reordering costs no extra tab stop. */
export const ReorderGrip = ({ className, placement = 'top' }: ReorderGripProps) => {
  const context = useContext(GripContext);
  // Throws rather than returning null. A missing provider is a wiring mistake,
  // and rendering nothing turns it into a board with no handles and a green
  // suite: a consumer spec that mounts the child alone asserts the grip and
  // finds nothing, or never asserts it at all.
  if (!context) throw new ComponentUsageError('outside_provider', 'ReorderGrip must be rendered inside a Reorder. Mount it from the render function passed as Reorder children, and wrap consumer specs in a Reorder too.');
  const title = context.labels.grip(context.name);

  return (
    <Tooltip placement={placement} title={title}>
      <button aria-label={title} className={joinClasses('bomdisoft-reorder-grip', className)} data-testid={`reorder-grip-${context.id}`} type='button' {...context.gripProps}>
        <svg aria-hidden='true' fill='currentColor' height='14' viewBox='0 0 10 14' width='10'>
          <circle cx='2.5' cy='3' r='1.2' />
          <circle cx='7.5' cy='3' r='1.2' />
          <circle cx='2.5' cy='7' r='1.2' />
          <circle cx='7.5' cy='7' r='1.2' />
          <circle cx='2.5' cy='11' r='1.2' />
          <circle cx='7.5' cy='11' r='1.2' />
        </svg>
      </button>
    </Tooltip>
  );
};
